import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/modern/utils';
import { Button } from '@/modern/components/ui/button';
import { Input } from '@/modern/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/modern/components/ui/popover';
import TaskEditorColorMapping from '@/other/constants/TaskEditorColors';
import { MIXED_TASK_COLOR } from '@/other/constants/TaskTypes';

// Mixed color palette for the gradient
const MIXED_COLORS = ['#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#5C6BC0', '#42A5F5', '#26C6DA', '#26A69A', '#66BB6A', '#FFCA28'];

interface ColorPickerProps {
  value?: string | null;
  onChange: (color: string | null) => void;
  clearable?: boolean;
  editable?: boolean;
  className?: string;
  triggerClassName?: string;
  children?: React.ReactNode;
  showMixedOption?: boolean; // New prop to enable mixed color option
  disabled?: boolean;
  hideDisplayValue?: boolean;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  clearable = false,
  editable = false,
  className,
  triggerClassName,
  children,
  showMixedOption = false,
  disabled = false,
  hideDisplayValue = false,
}) => {
  const [open, setOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value || '#000000');

  const handleColorSelect = (color: string | null) => {
    onChange(color);
    if (!editable) {
      setOpen(false);
    }
  };

  const handleCustomColorApply = () => {
    onChange(customColor);
    setOpen(false);
  };

  const renderColorPreview = () => {
    if (showMixedOption && value === MIXED_TASK_COLOR) {
      return (
        <div
          className="mr-2 h-5 w-5 rounded border border-border"
          style={{
            background: `linear-gradient(to right, ${MIXED_COLORS.join(', ')})`
          }}
        />
      );
    }

    return (
      <div
        className="mr-2 h-5 w-5 rounded border"
        style={{ backgroundColor: value || 'transparent' }}
      />
    );
  };

  const getDisplayValue = () => {
    if (showMixedOption && value === MIXED_TASK_COLOR) {
      return 'Mixed';
    }
    return value || 'Select color';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button
            variant="outline"
            className={cn('w-full justify-start', triggerClassName)}
            disabled={disabled}
          >
            {renderColorPreview()}
            {!hideDisplayValue && <span className={showMixedOption && value === MIXED_TASK_COLOR ? 'capitalize' : ''}>
                {getDisplayValue()}
              </span>
            }
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className={cn('w-80 p-3', className)} align="start">
        <div className="space-y-3">
          {/* Mixed Color Option */}
          {showMixedOption && (
            <button
              type="button"
              className={cn(
                'w-full h-10 rounded border border-border hover:scale-105 transition-transform relative flex items-center px-3',
                value === MIXED_TASK_COLOR && 'ring-2 ring-primary ring-offset-2'
              )}
              style={{
                background: `linear-gradient(to right, ${MIXED_COLORS.join(', ')})`
              }}
              onClick={() => handleColorSelect(MIXED_TASK_COLOR)}
              title="Mixed - Use task-specific colors"
            >
              <span className="text-white font-medium capitalize text-sm drop-shadow-md">
                Mixed
              </span>
              {value === MIXED_TASK_COLOR && (
                <Check className="absolute right-2 h-5 w-5 text-white drop-shadow-md" />
              )}
            </button>
          )}

          {/* Color Grid */}
          <div className="grid grid-cols-10 gap-1">
            {TaskEditorColorMapping.map((colorItem, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'h-7 w-7 rounded border border-border hover:scale-110 transition-transform relative',
                  value === colorItem.color && 'ring-2 ring-primary ring-offset-2'
                )}
                style={{ backgroundColor: colorItem.color }}
                onClick={() => handleColorSelect(colorItem.color)}
                title={colorItem.color}
              >
                {value === colorItem.color && (
                  <Check
                    className="absolute inset-0 m-auto h-4 w-4"
                    style={{ color: colorItem.text_color }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Clear Color Button */}
          {clearable && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleColorSelect(null)}
            >
              <i className="fa-regular fa-eraser mr-2" />
              Remove color
            </Button>
          )}

          {/* Custom Color Input */}
          {editable && (
            <div className="flex gap-2 pt-2 border-t">
              <Input
                type="text"
                placeholder="#000000"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="flex-1"
              />
              <Button type="button" onClick={handleCustomColorApply}>
                Apply
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};